/**
 * External Style Injection Helper
 *
 * Utilities for injecting styles from external Vue components
 * into Shadow DOM contexts. This is necessary because Shadow DOM
 * boundaries prevent external styles from penetrating.
 */

/**
 * Gets the shadow root for a given element
 *
 * @param targetElement - The element to find shadow root for
 * @returns The shadow root or null if not in a shadow DOM
 */
export const getShadowRoot = (targetElement: HTMLElement): ShadowRoot | null => {
    const root = targetElement.getRootNode();
    return root instanceof ShadowRoot ? root : null;
};

/**
 * Injects CSS styles into a shadow DOM or document
 *
 * @param shadowRoot - The shadow root or document to inject into
 * @param styles - CSS string to inject
 * @param id - Optional ID for the style element (for easier debugging)
 * @returns The created style element for cleanup
 *
 * @example
 * ```typescript
 * import { injectStyles, getShadowRoot } from '@/helpers/inject-external-styles';
 * import { extractChatStyles } from '@chatbot/utils/component-styles';
 *
 * const shadowRoot = getShadowRoot(containerRef.value);
 * const styles = extractChatStyles();
 * const styleElement = injectStyles(shadowRoot, styles, 'chatbot-styles');
 *
 * // Later, cleanup:
 * styleElement.remove();
 * ```
 */
const injectIntoShadowRoot = (shadowRoot: ShadowRoot,
    styleElement: HTMLStyleElement): void => {
    const { firstChild } = shadowRoot;
    if (firstChild) {
        shadowRoot.insertBefore(styleElement, firstChild);
    } else {
        shadowRoot.appendChild(styleElement);
    }
};

export const injectStyles = (
    shadowRoot: ShadowRoot | Document | null,
    styles: string,
    id?: string,
): HTMLStyleElement | null => {
    if (!shadowRoot) return null;

    const styleElement = document.createElement('style');

    if (id) {
        styleElement.setAttribute('data-style-id', id);
    }

    styleElement.textContent = styles;

    // Prepend to ensure base styles load first
    if (shadowRoot instanceof ShadowRoot) {
        injectIntoShadowRoot(shadowRoot, styleElement);
    } else if (shadowRoot instanceof Document) {
        shadowRoot.head.appendChild(styleElement);
    }

    return styleElement;
};

/**
 * Convenience function to inject chatbot styles into shadow DOM
 *
 * @param element - The element whose shadow root to inject into
 * @param stylesExtractor - Function that returns the CSS styles
 * @returns The created style element for cleanup, or null if failed
 *
 * @example
 * ```typescript
 * import { injectChatbotStyles } from '@/helpers/inject-external-styles';
 * import { extractChatStyles } from '@chatbot/utils/component-styles';
 *
 * const styleElement = injectChatbotStyles(
 *   chatContainerRef.value,
 *   extractChatStyles
 * );
 *
 * // Later, cleanup:
 * if (styleElement) {
 *   styleElement.remove();
 * }
 * ```
 */
export const injectChatbotStyles = (targetElement: HTMLElement | null,
    stylesExtractor: () => string): HTMLStyleElement | null => {
    if (!targetElement) return null;

    const shadowRoot = getShadowRoot(targetElement);
    const styles = stylesExtractor();

    return injectStyles(
        shadowRoot, styles, 'chatbot-styles',
    );
};
